import Typography from '@mui/material/Typography';
import { LeadListTypeIcon } from 'lead-list/components/LeadListTypeIcon';
import { PageHeading } from 'lead-list/components/PageHeading';
import { Paper } from 'lead-list/components/Paper';
import { GlobalState, useStateMachine } from 'little-state-machine';
import { useAuthContext } from 'providers/auth';
import { FC, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { Navigate, useNavigate, useParams } from 'react-router';
import { STEPS } from '../../LeadListCreate';
import { ListColumnMapper } from '../ListColumnMapper';
import { saveCachedMappings } from '../ListColumnMapper/leadListMappings';
import { Nav } from '../Nav';
import { StepContainer } from '../StepContainer';
import messages from './messages';

function updateMappings(state: GlobalState, mappings: { [key: string]: string }) {
  return {
    ...state,
    leadListCreate: {
      ...state.leadListCreate,
      mappings: {
        ...state.leadListCreate.mappings,
        ...mappings,
      },
    },
  };
}

function setHasSeenLeadType(state: GlobalState, hasSeen: boolean) {
  return {
    ...state,
    leadListCreate: {
      ...state.leadListCreate,
      hasSeenLeadType: hasSeen,
    },
  };
}

export const StepMapColumns: FC = () => {
  const { id: companyId } = useParams();
  const { isInternal } = useAuthContext();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();
  const { actions, state } = useStateMachine({ updateMappings, setHasSeenLeadType });
  const [goGreen] = useState(false);

  useEffect(() => {
    if (
      state.leadListCreate.file &&
      state.leadListCreate.fileHeaders &&
      state.leadListCreate.mappings
    ) {
      const t = setTimeout(() => actions.setHasSeenLeadType(true), 1000);
      return () => clearTimeout(t);
    }
  }, [
    actions,
    state.leadListCreate.file,
    state.leadListCreate.fileHeaders,
    state.leadListCreate.mappings,
  ]);

  const nextStep = () => {
    const { mappings, listType, campaign } = state.leadListCreate;
    const shortId = campaign?.shortId ?? null;

    if (shortId && mappings && listType) {
      saveCachedMappings(listType, mappings, shortId);
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
    navigate(`../${STEPS.review}`);
  };

  if (
    !state.leadListCreate.file ||
    !state.leadListCreate.fileHeaders ||
    !state.leadListCreate.mappings ||
    !state.leadListCreate.listType
  ) {
    if (isInternal && !companyId) return null;
    return <Navigate to=".." replace relative="path" />;
  }

  const effectiveValues = state.leadListCreate.fileHeaders
    .map((h) => state.leadListCreate.mappings?.[h])
    .filter((v): v is string => !!v && v !== 'unmapped');

  const assignedSet = useMemo(() => new Set(effectiveValues), [effectiveValues]);
  const mappingHasDuplicate = assignedSet.size !== effectiveValues.length;

  const mappingHasName = assignedSet.has('firstName') || assignedSet.has('fullName');
  const mappingHasCreated = assignedSet.has('created');
  const mappingHasPhone = assignedSet.has('phoneNumber');

  return (
    <StepContainer>
      <PageHeading title={formatMessage(messages.mappingTitle)} />

      <Typography variant="body1" sx={{ mb: 4 }}>
        {state.leadListCreate.file.name}
      </Typography>

      <Paper
        variant="outlined"
        shade={goGreen ? 'success' : undefined}
        sx={{ width: '100%', p: 3, position: 'relative' }}
      >
        <LeadListTypeIcon
          listType={state.leadListCreate.listType}
          sx={{ position: 'absolute', right: 24, top: 24 }}
        />

        <ListColumnMapper
          headers={state.leadListCreate.fileHeaders}
          onSetMappings={actions.updateMappings}
          mappings={state.leadListCreate.mappings}
          hasPhone={mappingHasPhone}
          hasName={mappingHasName}
          hasDuplicate={mappingHasDuplicate}
          hasCreated={mappingHasCreated}
          listType={state.leadListCreate.listType}
        />
      </Paper>

      <Nav
        onBackClick={() => navigate(-1)}
        onForwardClick={nextStep}
        disableForward={goGreen || mappingHasDuplicate || !mappingHasPhone}
        disableBack={goGreen}
      />
    </StepContainer>
  );
};
